
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { useAuth } from '@/contexts/auth';

interface ResetPasswordFormProps {
  token?: string;
  onBack: () => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
}

export const ResetPasswordForm = ({ token, onBack, isSubmitting, setIsSubmitting }: ResetPasswordFormProps) => {
  const { t } = useTranslation('auth');
  const { updatePassword } = useAuth();
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const formSchema = z.object({
    password: z.string().min(6, t("validation.passwordMinLength")),
    confirmPassword: z.string().min(6, t("validation.passwordMinLength")),
  }).refine((data) => data.password === data.confirmPassword, {
    message: t("validation.passwordsDoNotMatch"),
    path: ["confirmPassword"],
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    
    try {
      const result = await updatePassword(values.password);

      if (!result.success) {
        setError(result.error || t("resetPassword.error"));
        return;
      }

      setSuccess(t("resetPassword.success"));
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated. You can now log in with your new password.",
      });
      
      form.reset();
      
      // Redirect to login after a delay
      setTimeout(() => {
        onBack();
      }, 2000);
      
    } catch (error: unknown) {
      console.error("Reset password error:", error);
      const message = error instanceof Error ? error.message : t("resetPassword.error");
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center text-sm text-brand-slate">
        {t("resetPassword.description")}
      </div>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-center text-sm text-coral">
              {error}
            </div>
          )}
          
          {success && (
            <div className="rounded-md bg-teal-soft p-3 text-center text-sm text-teal">
              {success}
            </div>
          )}
          
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("resetPassword.password")}</FormLabel>
                <FormControl>
                  <Input type="password" placeholder={t("resetPassword.passwordPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("resetPassword.confirmPassword")}</FormLabel>
                <FormControl>
                  <Input type="password" placeholder={t("resetPassword.confirmPasswordPlaceholder")} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? t("resetPassword.resetting") : t("resetPassword.resetButton")}
          </Button>
        </form>
      </Form>
      
      <div className="mt-4 text-center">
        <Button 
          variant="link" 
          className="p-0 h-auto text-sm"
          onClick={onBack}
        >
          {t("resetPassword.backToLogin")}
        </Button>
      </div>
    </div>
  );
};
